import React, { ReactNode } from 'react';
import { mergeClasses, Text } from '@fluentui/react-components';
import { DailyHabitPlan } from 'src/models/dailyHabit/DailyHabitPlan';
import { DailyHabitPlanStatus } from 'src/utils/enums';

import { useStore } from 'src/hooks/useStore';
import { observer } from 'mobx-react-lite';
import FrownIcon from 'src/components/atoms/webmdhs-icon/frown-icon/FrownIcon';
import BlankFaceIcon from 'src/components/atoms/webmdhs-icon/blank-face-icon/BlankFaceIcon';
import SmileIcon from 'src/components/atoms/webmdhs-icon/smile-icon/SmileIcon';
import DailyHabitsIcon from 'src/components/molecules/daily-habits-icon/DailyHabitsIcon';
import NavigationTabPanelHeader from 'src/components/molecules/navigation-tab-panel-header/NavigationTabPanelHeader';
import MilestoneProgressBar from 'src/components/molecules/milestone-progress-bar/MilestoneProgressBar';
import HomeWidgetTextStore from 'src/stores/HomeWidgetTextStore';
import NavigationTabPanel from '../../molecules/navigation-tab-panel/NavigationTabPanel';
import CircularProgress from '../../atoms/circular-progress/CircularProgress';
import { usePlanHabitDetailsStyles } from './PlanHabitDetails.styles';

interface PlanHabitDetailsProps {
  plan: DailyHabitPlan
  name: string
}

const getStatusTextColorClass = (planStatus: DailyHabitPlanStatus, styles: ReturnType<typeof usePlanHabitDetailsStyles>): string => {
  switch (planStatus) {
    case DailyHabitPlanStatus.OffTrack: return styles.statusOffTrack;
    case DailyHabitPlanStatus.AtRisk: return styles.statusAtRisk;
    default: return styles.statusOnTrack;
  }
};

const getStatusIcon = (planStatus: DailyHabitPlanStatus, styles: ReturnType<typeof usePlanHabitDetailsStyles>): ReactNode => {
  switch (planStatus) {
    case DailyHabitPlanStatus.OffTrack: return (
      <span className={mergeClasses(styles.statusIcon, styles.statusIconOffTrack)}>
        <FrownIcon />
      </span>
    );
    case DailyHabitPlanStatus.AtRisk: return (
      <span className={mergeClasses(styles.statusIcon, styles.statusIconAtRisk)}>
        <BlankFaceIcon />
      </span>
    );
    default: return (
      <span className={mergeClasses(styles.statusIcon, styles.statusIconOnTrack)}>
        <SmileIcon />
      </span>
    );
  }
};

const getPlanStatusText = (planStatus: DailyHabitPlanStatus, homeWidgetTextStore: HomeWidgetTextStore): string => {
  switch (planStatus) {
    case DailyHabitPlanStatus.OffTrack:
      return homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:Status:TextOffTrack');
    case DailyHabitPlanStatus.AtRisk:
      return homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:Status:TextAtRisk');
    default:
      return homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:Status:TextOnTrack');
  }
};

const getVisibleProgressMilestones = (milestones: number[], overallProgressPct: number): number[] => {
  const sortedMilestones = [...milestones].sort((a, b) => a - b);
  const completedMarkers = sortedMilestones.filter(marker => marker <= overallProgressPct);
  const nextVisibleMarker = sortedMilestones.find(marker => marker > overallProgressPct);
  return nextVisibleMarker ? [...completedMarkers, nextVisibleMarker] : completedMarkers;
};

const getProgressTemplate = (
  percentComplete: number,
  daysCompleted: number,
  homeWidgetTextStore: HomeWidgetTextStore,
): string => {
  if (daysCompleted === 0) {
    return homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:ProgressMessage:NoProgress');
  }

  const thresholds = [
    { limit: 25, key: 'LessThan25' },
    { limit: 50, key: 'LessThan50' },
    { limit: 75, key: 'LessThan75' },
  ];

  if ([25, 50, 75].includes(percentComplete)) {
    return homeWidgetTextStore.getHomeWidgetText(`DailyHabitsWidget:ProgressMessage:${percentComplete}`);
  }

  const threshold = thresholds.find(({ limit }) => percentComplete < limit);
  return homeWidgetTextStore.getHomeWidgetText(
    `DailyHabitsWidget:ProgressMessage:${threshold?.key ?? 'MoreThan75'}`,
  );
};

export const getProgressPercentMessage = (
  percentComplete: number,
  daysCompleted: number,
  homeWidgetTextStore: HomeWidgetTextStore,
): string => {
  const template = getProgressTemplate(percentComplete, daysCompleted, homeWidgetTextStore);
  return template.replace('{__PERCENT_COMPLETE__}', `${percentComplete}`);
};

const PlanHabitDetails: React.FC<PlanHabitDetailsProps> = ({
  plan,
  name,
}) => {
  const { homeWidgetTextStore } = useStore();
  const styles = usePlanHabitDetailsStyles();

  const overallProgressPct = plan.overallProgressPct ?? 0;

  const planStatusText = getPlanStatusText(plan.status, homeWidgetTextStore);
  const statusIcon = getStatusIcon(plan.status, styles);
  const statusColorClass = getStatusTextColorClass(plan.status, styles);

  const [statusDescription, statusMessage] = homeWidgetTextStore.getHomeWidgetText(
    'DailyHabitsWidget:Communications:WarningStatus',
  )?.split('{__PLAN_STATUS__}') ?? [];

  const progressPercentMessage = getProgressPercentMessage(overallProgressPct, plan.daysRemaining, homeWidgetTextStore);

  const daysRemainingText = homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:Plan:DaysRemaining');
  const titleHeader = `${name} ${homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:Plan:Plan')}`;

  const currentProgress = overallProgressPct;
  const visibleMilestones = getVisibleProgressMilestones(plan.milestones || [], currentProgress);

  const body = (
    <div className={styles.progressSection}>
      <div className={styles.progressRow}>
        <div
          className={styles.progressColumn}
          aria-label={planStatusText}
        >
          <div className={styles.progressWrapper} data-testid="daily-habit-plan-detail-status-icon">
            {statusIcon}
          </div>
          <Text className={styles.label} data-testid="daily-habit-plan-detail-status-label">{planStatusText}</Text>
        </div>

        <div
          className={styles.progressColumn}
          aria-label={`${plan.daysRemaining} ${daysRemainingText}`}
        >
          <div className={styles.progressWrapper}>
            <CircularProgress
              progress={overallProgressPct}
            >
              <span data-testid="daily-habit-plan-detail-progress-value">{plan.daysRemaining}</span>
            </CircularProgress>
          </div>

          <Text className={styles.label} data-testid="daily-habit-plan-detail-progress-days-remaining-label">
            {daysRemainingText}
          </Text>
        </div>
      </div>

      <div className={styles.statusRow}>
        <Text className={styles.statusText} data-testid="daily-habit-plan-detail-status-message">
          {statusDescription}

          <span className={statusColorClass} data-testid="daily-habit-plan-detail-status-message-highlight">
            {planStatusText}
          </span>
          {statusMessage}
        </Text>
      </div>
    </div>
  );

  const footer = (
    <MilestoneProgressBar
      milestones={visibleMilestones}
      currentProgress={currentProgress}
      text={progressPercentMessage}
    />
  );

  return (
    <NavigationTabPanel
      header={(
        <NavigationTabPanelHeader
          icon={<DailyHabitsIcon planId={plan.id} />}
          text={titleHeader}
        />
      )}
      footer={footer}
    >
      {body}
    </NavigationTabPanel>
  );
};

export default observer(PlanHabitDetails);
